import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  PauseIcon,
  PlayIcon,
  ForwardIcon,
  ArrowPathIcon,
  XMarkIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { useDemo, DEMO_BID_TEMPLATE } from './DemoContext';
import { BID_DEMO_STEPS } from './bidDemoSteps';

const SPOTLIGHT_PADDING = 10;

const waitForElement = (selector, maxMs = 4000) =>
  new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      const el = document.querySelector(selector);
      if (el) return resolve(el);
      if (Date.now() - started >= maxMs) return resolve(null);
      requestAnimationFrame(tick);
    };
    tick();
  });

const useElementRect = (selector, depKey) => {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let cleanup   = () => {};

    if (!selector) {
      setRect(null);
      return cleanup;
    }

    (async () => {
      const el = await waitForElement(selector, 6000);
      if (cancelled || !el) {
        if (!cancelled) setRect(null);
        return;
      }

      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      } catch {
        // some elements (e.g. SVG inside transformed parents) may throw — ignore
      }

      const update = () => {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      };
      update();

      const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
      ro?.observe(el);
      window.addEventListener('resize', update);
      window.addEventListener('scroll', update, true);

      cleanup = () => {
        ro?.disconnect();
        window.removeEventListener('resize', update);
        window.removeEventListener('scroll', update, true);
      };
    })();

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [selector, depKey]);

  return rect;
};

const Spotlight = ({ rect }) => {
  if (!rect) {
    return (
      <div
        className="fixed inset-0 pointer-events-auto"
        style={{ background: 'rgba(15, 23, 42, 0.55)' }}
      />
    );
  }

  const pad = SPOTLIGHT_PADDING;
  const top    = Math.max(0, rect.top - pad);
  const left   = Math.max(0, rect.left - pad);
  const width  = rect.width  + pad * 2;
  const height = rect.height + pad * 2;

  return (
    <>
      {/* 4 dim panels around the cutout */}
      <div className="fixed pointer-events-auto" style={{ top: 0, left: 0, right: 0, height: top, background: 'rgba(15, 23, 42, 0.55)' }} />
      <div className="fixed pointer-events-auto" style={{ top, left: 0, width: left, height, background: 'rgba(15, 23, 42, 0.55)' }} />
      <div className="fixed pointer-events-auto" style={{ top, left: left + width, right: 0, height, background: 'rgba(15, 23, 42, 0.55)' }} />
      <div className="fixed pointer-events-auto" style={{ top: top + height, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.55)' }} />

      {/* Glowing ring around the target */}
      <div
        className="fixed pointer-events-none rounded-lg"
        style={{
          top, left, width, height,
          boxShadow: '0 0 0 3px rgba(168, 85, 247, 0.95), 0 0 24px 6px rgba(168, 85, 247, 0.55)',
          transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />
    </>
  );
};

const CaptionCard = ({
  step,
  index,
  total,
  isPaused,
  onPause,
  onResume,
  onBack,
  onSkip,
  onRestart,
  onExit,
}) => (
  <div
    className="fixed bottom-6 right-6 z-[10001] w-[360px] max-w-[calc(100vw-3rem)] rounded-xl bg-white shadow-2xl border border-purple-200 overflow-hidden pointer-events-auto"
    role="dialog"
    aria-live="polite"
  >
    <div className="px-4 py-2.5 bg-gradient-to-r from-purple-600 to-violet-600 text-white flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-wider opacity-90">Demo</span>
        <span className="text-xs font-medium opacity-80">{index + 1} / {total}</span>
      </div>
      <button
        onClick={onExit}
        className="p-1 rounded hover:bg-white/15 transition-colors"
        title="Exit demo"
      >
        <XMarkIcon className="w-4 h-4" />
      </button>
    </div>

    <div className="p-4">
      <h3 className="text-sm font-bold text-gray-900 mb-1.5">{step.title}</h3>
      <p className="text-sm text-gray-600 leading-relaxed">{step.narration}</p>

      <div className="mt-2 h-1 bg-purple-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-purple-500 transition-all duration-300"
          style={{ width: `${((index + 1) / total) * 100}%` }}
        />
      </div>
    </div>

    <div className="px-3 py-2 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
      <div className="flex items-center gap-1">
        <button
          onClick={onBack}
          disabled={index === 0}
          className="p-1.5 rounded text-gray-600 hover:bg-white hover:text-purple-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          title="Back"
        >
          <ArrowLeftIcon className="w-4 h-4" />
        </button>
        {isPaused ? (
          <button
            onClick={onResume}
            className="p-1.5 rounded text-gray-600 hover:bg-white hover:text-purple-600 transition-colors"
            title="Resume"
          >
            <PlayIcon className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={onPause}
            className="p-1.5 rounded text-gray-600 hover:bg-white hover:text-purple-600 transition-colors"
            title="Pause"
          >
            <PauseIcon className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={onSkip}
          className="p-1.5 rounded text-gray-600 hover:bg-white hover:text-purple-600 transition-colors"
          title="Next"
        >
          <ForwardIcon className="w-4 h-4" />
        </button>
        <button
          onClick={onRestart}
          className="p-1.5 rounded text-gray-600 hover:bg-white hover:text-purple-600 transition-colors"
          title="Restart"
        >
          <ArrowPathIcon className="w-4 h-4" />
        </button>
      </div>

      <button
        onClick={onExit}
        className="text-xs font-medium text-gray-500 hover:text-red-600 transition-colors px-2 py-1 rounded"
      >
        Exit
      </button>
    </div>
  </div>
);

const BidTemplateDemo = () => {
  const {
    activeDemo,
    stepIndex,
    setStepIndex,
    isPaused,
    pause,
    resume,
    skip,
    back,
    restart,
    exit,
  } = useDemo();
  const location = useLocation();

  const isOnBidPage = location.pathname.startsWith('/dashboard/bid_template');
  const isRunning   = activeDemo === DEMO_BID_TEMPLATE && isOnBidPage;

  const total       = BID_DEMO_STEPS.length;
  const currentStep = BID_DEMO_STEPS[stepIndex] || null;
  const rect        = useElementRect(isRunning ? currentStep?.selector : null, stepIndex);

  // Auto-advance + click action
  const advanceTimerRef = useRef(null);
  const actionFiredRef  = useRef(false);

  useEffect(() => {
    actionFiredRef.current = false;
  }, [stepIndex]);

  useEffect(() => {
    if (!isRunning || !currentStep) return undefined;
    if (isPaused) return undefined;

    let cancelled = false;

    const run = async () => {
      const el = await waitForElement(currentStep.selector, currentStep.waitForMs ?? 4000);
      if (cancelled) return;

      // For 'click' steps, fire the click partway through the dwell so the user
      // sees the highlight first, then the action.
      const dwell = currentStep.dwellMs ?? 3500;

      if (currentStep.action === 'click' && el && !actionFiredRef.current) {
        actionFiredRef.current = true;
        const clickAt = Math.max(800, dwell - 1200);
        setTimeout(() => {
          if (cancelled) return;
          try { el.click(); } catch (e) { /* ignore */ }
        }, clickAt);
      }

      advanceTimerRef.current = setTimeout(() => {
        if (cancelled) return;
        setStepIndex((i) => {
          if (i >= total - 1) return i; // stop at last step
          return i + 1;
        });
      }, dwell);
    };

    run();

    return () => {
      cancelled = true;
      if (advanceTimerRef.current) {
        clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = null;
      }
    };
  }, [isRunning, isPaused, currentStep, stepIndex, total, setStepIndex]);

  if (!isRunning || !currentStep) return null;

  return (
    <div className="fixed inset-0 z-[10000] pointer-events-none">
      {/* Spotlight + dim backdrop. Backdrop has pointer-events: auto to block real
          clicks, but JS .click() bypasses pointer-events so the demo can still drive. */}
      <Spotlight rect={rect} />

      <CaptionCard
        step={currentStep}
        index={stepIndex}
        total={total}
        isPaused={isPaused}
        onPause={pause}
        onResume={resume}
        onBack={back}
        onSkip={() => skip(total)}
        onRestart={restart}
        onExit={exit}
      />
    </div>
  );
};

export default BidTemplateDemo;
